// NEW_FILE_JUSTIFIED: customer self-service policy application page — new route /my-insurance/apply/new
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/customer/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/customer/ require three levels up to reach convex/_generated/
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { PolicyApplicationWizard } from '@/components/insurance/policy-application/PolicyApplicationWizard';
import { InsuranceTypeCard } from '@/components/insurance/InsuranceTypeCard';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useCompanyContext } from '@/hooks/useCompanyContext';
import {
  useInsuranceTypeSelection,
  getInsuranceTypeConfig,
} from '@/hooks/useInsuranceTypeSelection';
import type { IPolicyFormTemplate } from '@/@types/policyFormTemplates';
import type { IOrgInsuranceType } from '@/@types/insurance';

function toOrgId(raw: string): Id<'organisations'> {
  return raw as Id<'organisations'>; // AS_JUSTIFIED: raw comes from currentOrg._id or currentCompany.orgId, both are typed Id<'organisations'> at the Convex query level
}

export function CustomerApplyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get('draftId');

  const [selectedInsuranceTypeId, setSelectedInsuranceTypeId] =
    useState<Id<'orgInsuranceTypes'> | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const { currentCompany, isLoading: companyLoading } = useCompanyContext();

  const isContextLoading = orgLoading || companyLoading;
  const rawOrgId = currentOrg?._id ?? currentCompany?.orgId ?? null;
  const orgId: Id<'organisations'> | null = rawOrgId ? toOrgId(rawOrgId) : null;

  const isCompanyEmployee = !!currentCompany && !currentOrg;
  const policySource = isCompanyEmployee ? 'company_employer' : 'personal';
  const backPath = isCompanyEmployee ? '/company/my-insurance' : '/my-insurance';

  const orgTypesRaw = useQuery(api['insurance/types'].listOrgTypes, orgId ? { orgId } : 'skip');
  const orgTypes = orgTypesRaw as IOrgInsuranceType[] | undefined; // AS_JUSTIFIED: listOrgTypes uses dynamic returns — cast to IOrgInsuranceType[] for rendering

  const templatesRaw = useQuery(
    api['insurance/policyFormTemplates'].list,
    orgId ? { orgId } : 'skip'
  );
  const templates = templatesRaw as IPolicyFormTemplate[] | undefined; // AS_JUSTIFIED: policyFormTemplates.list uses returns: dynamic — cast to IPolicyFormTemplate[] for component rendering

  const draftData = useQuery(
    api['insurance/policyApplications'].getApplicationData,
    draftIdParam ? { policyId: draftIdParam as Id<'insurancePolicies'> } : 'skip' // AS_JUSTIFIED: draftId URL param from MyApplicationsPage navigation with valid policy ID
  );

  const {
    visibleOrgTypes,
    filteredOrgTypes,
    uniqueTypeNames,
    filterType,
    setFilterType,
    hasExplicitEmptyAllowList,
    templatesForSelectedType,
    autoSelectedTemplate,
  } = useInsuranceTypeSelection({
    orgTypes,
    templates,
    policySource,
    allowedInsuranceTypeIds: currentCompany?.allowedInsuranceTypeIds,
    selectedInsuranceTypeId,
  });

  // When resuming a draft, auto-select the template from draft data
  const draftTemplate = draftData?.templateId
    ? templates?.find(t => t._id === draftData.templateId)
    : undefined;

  // Determine the active template: draft resume > auto-selected (1 match) > manual secondary picker
  const manuallySelectedTemplate = templatesForSelectedType.find(t => t._id === selectedTemplateId);
  const selectedTemplate =
    draftTemplate ?? autoSelectedTemplate ?? manuallySelectedTemplate ?? null;

  const isDataLoading = orgTypesRaw === undefined || templatesRaw === undefined;

  if (isContextLoading) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-3 gap-4 w-full">
          <Skeleton className="h-64 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!orgId) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
            <ArrowLeft aria-hidden="true" className="mr-1 h-4 w-4" />
            Back to My Insurance
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Unable to determine your organisation. Please contact support.
        </p>
      </div>
    );
  }

  // Show the wizard when a template is selected (draft resume or card selection)
  if (selectedTemplate) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedInsuranceTypeId(null);
              setSelectedTemplateId('');
            }}
          >
            <ArrowLeft aria-hidden="true" className="mr-1 h-4 w-4" />
            Back to Insurance Types
          </Button>
          <h1 className="text-2xl font-semibold text-foreground">Apply for Insurance</h1>
        </div>
        <PolicyApplicationWizard
          template={selectedTemplate}
          orgId={orgId}
          policySource={policySource}
          existingPolicyId={draftData?._id}
          existingData={draftData?.applicationData}
          policyStatus={draftData?.status}
          rejectionReason={draftData?.rejectionReason}
          onComplete={() => navigate(backPath)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      {/* Back navigation */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
          <ArrowLeft aria-hidden="true" className="mr-1 h-4 w-4" />
          Back to My Insurance
        </Button>
      </div>

      {/* Page heading */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-foreground mb-2">
          What type of insurance do you need?
        </h1>
        <p className="text-sm text-muted-foreground">
          Select the insurance type below to start your application.
        </p>
      </div>

      {/* Filter tabs + Card grid */}
      <div className="w-full">
        {isDataLoading ? (
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-64 w-full rounded-2xl" />
            <Skeleton className="h-64 w-full rounded-2xl" />
            <Skeleton className="h-64 w-full rounded-2xl" />
            <Skeleton className="h-64 w-full rounded-2xl" />
          </div>
        ) : hasExplicitEmptyAllowList ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No insurance types are currently available for your company.
          </p>
        ) : visibleOrgTypes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No application forms are available at this time. Please contact your insurance provider.
          </p>
        ) : (
          <>
            {uniqueTypeNames.length > 1 && (
              <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                <Button
                  variant={filterType === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'rounded-full px-4',
                    filterType === 'all'
                      ? 'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90'
                      : 'hover:bg-sidebar-primary hover:text-sidebar-primary-foreground dark:hover:bg-sidebar-primary dark:hover:text-sidebar-primary-foreground'
                  )}
                  onClick={() => setFilterType('all')}
                >
                  All
                </Button>
                {uniqueTypeNames.map(name => (
                  <Button
                    key={name}
                    variant={filterType === name ? 'default' : 'outline'}
                    size="sm"
                    className={cn(
                      'rounded-full px-4',
                      filterType === name
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90'
                        : 'hover:bg-sidebar-primary hover:text-sidebar-primary-foreground dark:hover:bg-sidebar-primary dark:hover:text-sidebar-primary-foreground'
                    )}
                    onClick={() => setFilterType(name)}
                  >
                    {name.replace(' Insurance', '')}
                  </Button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              {filteredOrgTypes.map(orgType => {
                const config = getInsuranceTypeConfig(orgType.name);
                return (
                  <InsuranceTypeCard
                    key={orgType._id}
                    name={orgType.name}
                    description={config.description}
                    iconName={config.iconName}
                    iconColor={config.iconColor}
                    iconBgColor={config.iconBgColor}
                    features={config.features}
                    isPopular={config.isPopular}
                    onSelect={() => setSelectedInsuranceTypeId(orgType._id)}
                  />
                );
              })}
            </div>

            {/* Secondary template picker: shown when a type is selected but has multiple templates */}
            {selectedInsuranceTypeId && templatesForSelectedType.length > 1 && (
              <div className="mt-6 max-w-sm mx-auto">
                <p className="text-sm font-medium text-foreground mb-2">
                  Multiple forms available — select one to continue:
                </p>
                <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an application form..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templatesForSelectedType.map(t => (
                      <SelectItem key={t._id} value={t._id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* No templates for the selected type */}
            {selectedInsuranceTypeId && templatesForSelectedType.length === 0 && (
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  No application forms available for this insurance type.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
